import { useMemo } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

const COLORS = ['#059669', '#0EA5A4', '#2563EB', '#7C3AED', '#EC4899', '#F97316', '#EAB308', '#10B981', '#06B6D4', '#8B5CF6'];

function eur(n) { return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(Math.round(n || 0)); }

export default function ResultsPanel({ partidas, surface, baselineStandard }) {
  const incluidas = partidas.filter(p => p.incluir);
  const subtotal = incluidas.reduce((s, p) => s + p.precioFinal * surface, 0);
  const iva = subtotal * 0.10; // IVA reducido 10% reformas vivienda habitual
  const total = subtotal + iva;
  const precioM2 = surface > 0 ? subtotal / surface : 0;

  const totalEstandar = useMemo(
    () => incluidas.reduce((s, p) => s + (baselineStandard[p.id] ?? p.estandar) * surface, 0),
    [incluidas, baselineStandard, surface]
  );
  const ahorroTotal = Math.max(0, totalEstandar - subtotal);
  const ahorroPct = totalEstandar > 0 ? (ahorroTotal / totalEstandar) * 100 : 0;

  const data = incluidas
    .map(p => ({ name: p.name, value: p.precioFinal * surface }))
    .filter(d => d.value > 0)
    .sort((a, b) => b.value - a.value);

  return (
    <div className="space-y-4">
      {/* Total destacado */}
      <div className="relative overflow-hidden rounded-2xl bg-emerald-600 text-white p-6 md:p-7 shadow-[0_20px_60px_-20px_rgba(5,150,105,0.5)]" data-testid="pro-total-card">
        <div className="absolute -right-12 -top-12 w-56 h-56 rounded-full bg-white/10 blur-2xl" />
        <div className="relative">
          <div className="text-[11px] uppercase tracking-[0.25em] font-semibold text-emerald-100">Presupuesto total (con IVA 10%)</div>
          <div className="flex items-baseline gap-2 mt-3 flex-wrap">
            <div className="font-display font-bold text-4xl md:text-5xl leading-none tracking-tighter tabular-nums" data-testid="pro-total-amount">
              {eur(total)}
            </div>
          </div>
          <div className="text-emerald-50/90 text-sm mt-3 grid grid-cols-2 gap-y-1">
            <span>Subtotal (sin IVA):</span><span className="text-right font-semibold tabular-nums">{eur(subtotal)}</span>
            <span>IVA 10%:</span><span className="text-right font-semibold tabular-nums">{eur(iva)}</span>
            <span>Precio medio /m²:</span><span className="text-right font-semibold tabular-nums">{eur(precioM2)}</span>
            <span>Superficie:</span><span className="text-right font-semibold">{surface} m²</span>
          </div>
        </div>
      </div>

      {/* Ahorro vs estándar */}
      {ahorroTotal > 0 && (
        <div className="rounded-2xl border border-emerald-200 dark-border bg-emerald-50 dark-bg-section p-5" data-testid="pro-savings-card">
          <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 dark-text-accent font-semibold">Ahorro vs versión Estándar</div>
          <div className="flex items-baseline gap-3 mt-2 flex-wrap">
            <div className="font-display font-bold text-2xl md:text-3xl text-emerald-700 dark-text-accent tabular-nums">{eur(ahorroTotal)}</div>
            <div className="text-emerald-700/80 dark-text-accent text-sm font-semibold">−{ahorroPct.toFixed(1)}%</div>
          </div>
          <div className="text-xs text-emerald-800/80 dark-text-mid mt-1">
            Total estándar: {eur(totalEstandar)} → Total ajustado: {eur(subtotal)}
          </div>
        </div>
      )}

      {/* Pie chart */}
      <div className="rounded-2xl border border-slate-200 dark-border bg-white dark-bg-card p-5" data-testid="pro-distribution-chart">
        <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 dark-text-mid font-semibold mb-3">Distribución de costes</div>
        {data.length === 0 ? (
          <div className="text-sm text-slate-400 dark-text-mid py-10 text-center">Activa alguna partida para ver el gráfico</div>
        ) : (
          <>
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={2}>
                    {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip
                    formatter={(v) => eur(v)}
                    contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 space-y-1.5">
              {data.slice(0, 6).map((d, i) => {
                const pct = subtotal > 0 ? (d.value / subtotal) * 100 : 0;
                return (
                  <div key={d.name} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                      <span className="text-slate-700 dark-text-mid truncate">{d.name}</span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-slate-500 dark-text-mid tabular-nums">{pct.toFixed(0)}%</span>
                      <span className="text-slate-900 dark-text-strong font-semibold tabular-nums w-16 text-right">{eur(d.value)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
